import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  PersonAdd as PromoteIcon,
  Block as DeactivateIcon,
  CheckCircle as ActivateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  LockReset as ResetPasswordIcon
} from '@mui/icons-material';
import { useProfile } from '../../contexts/ProfileContext';
import { hasSystemPermission, SYSTEM_PERMISSIONS, SYSTEM_ROLES, DEFAULT_ROLE_PERMISSIONS, getSystemRoleDisplayName } from '../../utils/systemPermissions';
import {
  getAllUsers,
  searchUsers,
  getUserById,
  updateUserProfile,
  promoteToSystemAdmin,
  deactivateUser,
  reactivateUser,
  getUserStats
} from '../../services/userManagementService';
import { formatDistanceToNow } from 'date-fns';
import { getFunctions, httpsCallable } from 'firebase/functions';
import logger from '../../utils/logger';

/**
 * SystemUserManagement Component
 * Manage all users and system administrators
 */
const SystemUserManagement = () => {
  const { profile } = useProfile();

  // States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [stats, setStats] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Modals
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [createAccountDialogOpen, setCreateAccountDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [editForm, setEditForm] = useState({});
  const [promoteForm, setPromoteForm] = useState({
    role: SYSTEM_ROLES.SYSTEM_ADMIN,
    permissions: {}
  });
  const [createForm, setCreateForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: SYSTEM_ROLES.SYSTEM_ADMIN,
    permissions: {},
    assignedFamilies: []
  });
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Alerts
  const [alert, setAlert] = useState(null);

  // Check permissions
  const canViewUsers = hasSystemPermission(profile, SYSTEM_PERMISSIONS.VIEW_USERS);
  const canManageUsers = hasSystemPermission(profile, SYSTEM_PERMISSIONS.MANAGE_USERS);
  const canManageSystemAdmins = hasSystemPermission(profile, SYSTEM_PERMISSIONS.MANAGE_SYSTEM_ADMINS);

  // Load users and stats
  useEffect(() => {
    if (canViewUsers) {
      loadUsers();
      loadStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewUsers]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await getAllUsers(rowsPerPage);
      setUsers(result.users);
      setLoading(false);
    } catch (error) {
      logger.error('Error loading users:', error);
      setAlert({ severity: 'error', message: 'Failed to load users' });
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const userStats = await getUserStats();
      setStats(userStats);
    } catch (error) {
      logger.error('Error loading stats:', error);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        const results = await searchUsers(searchTerm);
        setUsers(results);
        setLoading(false);
      } catch (error) {
        logger.error('Error searching users:', error);
        setAlert({ severity: 'error', message: 'Failed to search users' });
        setLoading(false);
      }
    } else {
      loadUsers();
    }
  };

  const handleViewUser = async (user) => {
    try {
      const fullUser = await getUserById(user.id);
      setSelectedUser(fullUser);
      setViewDialogOpen(true);
    } catch (error) {
      logger.error('Error viewing user:', error);
      setAlert({ severity: 'error', message: 'Failed to load user details' });
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      displayName: user.displayName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await updateUserProfile(selectedUser.id, editForm);
      setAlert({ severity: 'success', message: 'User updated successfully' });
      setEditDialogOpen(false);
      loadUsers();
      loadStats();
    } catch (error) {
      logger.error('Error updating user:', error);
      setAlert({ severity: 'error', message: 'Failed to update user' });
    }
  };

  const handlePromoteUser = (user) => {
    setSelectedUser(user);
    setPromoteForm({
      role: SYSTEM_ROLES.SYSTEM_ADMIN,
      permissions: { ...DEFAULT_ROLE_PERMISSIONS[SYSTEM_ROLES.SYSTEM_ADMIN] }
    });
    setPromoteDialogOpen(true);
  };

  const handleRoleChange = (newRole) => {
    setPromoteForm({
      role: newRole,
      permissions: { ...DEFAULT_ROLE_PERMISSIONS[newRole] }
    });
  };

  const handlePermissionToggle = (permissionKey) => {
    setPromoteForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionKey]: !prev.permissions[permissionKey]
      }
    }));
  };

  const handleSavePromote = async () => {
    try {
      await promoteToSystemAdmin(selectedUser.id, promoteForm.role, promoteForm.permissions);
      setAlert({ severity: 'success', message: 'User promoted to system admin successfully' });
      setPromoteDialogOpen(false);
      loadUsers();
      loadStats();
    } catch (error) {
      logger.error('Error promoting user:', error);
      setAlert({ severity: 'error', message: 'Failed to promote user' });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive === false) {
        await reactivateUser(user.id);
        setAlert({ severity: 'success', message: 'User reactivated successfully' });
      } else {
        await deactivateUser(user.id);
        setAlert({ severity: 'success', message: 'User deactivated successfully' });
      }
      loadUsers();
      loadStats();
    } catch (error) {
      logger.error('Error toggling user status:', error);
      setAlert({ severity: 'error', message: 'Failed to update user status' });
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!window.confirm(`Send a password reset email to ${user.email}?`)) {
      return;
    }

    try {
      const functions = getFunctions();
      const sendPasswordResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendPasswordResetEmail({ email: user.email });
      setAlert({ severity: 'success', message: `Password reset email sent to ${user.email}` });
    } catch (error) {
      logger.error('Error sending password reset:', error);
      setAlert({ severity: 'error', message: 'Failed to send password reset email. The user may need to use the "Forgot Password" link on the login page.' });
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({
      email: '',
      firstName: '',
      lastName: '',
      role: SYSTEM_ROLES.SYSTEM_ADMIN,
      permissions: { ...DEFAULT_ROLE_PERMISSIONS[SYSTEM_ROLES.SYSTEM_ADMIN] },
      assignedFamilies: []
    });
    setCreateAccountDialogOpen(true);
  };

  const handleCreateRoleChange = (newRole) => {
    setCreateForm({
      ...createForm,
      role: newRole,
      permissions: { ...DEFAULT_ROLE_PERMISSIONS[newRole] }
    });
  };

  const handleCreatePermissionToggle = (permissionKey) => {
    setCreateForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionKey]: !prev.permissions[permissionKey]
      }
    }));
  };

  const handleCreateAccount = async () => {
    try {
      setCreatingAccount(true);

      // Call Cloud Function to create system account
      const functions = getFunctions();
      const createSystemAccount = httpsCallable(functions, 'createSystemAccount');

      const result = await createSystemAccount({
        email: createForm.email,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        accountType: 'system_admin',
        role: createForm.role,
        permissions: createForm.permissions,
        assignedFamilies: createForm.assignedFamilies
      });

      setAlert({ severity: 'success', message: `System account created successfully. Temporary password: ${result.data.tempPassword}` });
      setCreateAccountDialogOpen(false);
      loadUsers();
      loadStats();
    } catch (error) {
      logger.error('Error creating system account:', error);
      setAlert({ severity: 'error', message: error.message || 'Failed to create system account' });
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Reload with new page size
    loadUsers();
  };

  // Filter users based on role filter
  const filteredUsers = filterRole === 'all'
    ? users
    : filterRole === 'system_admins'
    ? users.filter(u => u.role && ['root_admin', 'system_admin', 'support_agent', 'developer', 'ux_designer'].includes(u.role))
    : filterRole === 'family_users'
    ? users.filter(u => u.familyCode)
    : filterRole === 'no_family'
    ? users.filter(u => !u.familyCode)
    : users;

  if (!canViewUsers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to view users</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canManageSystemAdmins && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              variant="contained"
              color="primary"
            >
              Create System Account
            </Button>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => { loadUsers(); loadStats(); }}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Total Users</Typography>
                <Typography variant="h4">{stats.totalUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Active Users</Typography>
                <Typography variant="h4">{stats.activeUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">System Admins</Typography>
                <Typography variant="h4">{stats.systemAdmins}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">With Family</Typography>
                <Typography variant="h4">{stats.usersWithFamily}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'grey.800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">No Family</Typography>
                <Typography variant="h4">{stats.usersWithoutFamily}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.800' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchTerm(''); loadUsers(); }}>
                      ×
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Role</InputLabel>
              <Select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                label="Filter by Role"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="system_admins">System Admins</MenuItem>
                <MenuItem value="family_users">Users with Family</MenuItem>
                <MenuItem value="no_family">Users without Family</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* User Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'grey.800' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'grey.100' }}>Name</TableCell>
              <TableCell sx={{ color: 'grey.100' }}>Email</TableCell>
              <TableCell sx={{ color: 'grey.100' }}>Family Code</TableCell>
              <TableCell sx={{ color: 'grey.100' }}>Role</TableCell>
              <TableCell sx={{ color: 'grey.100' }}>Status</TableCell>
              <TableCell sx={{ color: 'grey.100' }}>Created</TableCell>
              <TableCell align="right" sx={{ color: 'grey.100' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.id} hover sx={{ '&:hover': { bgcolor: 'grey.700' } }}>
                  <TableCell sx={{ color: 'grey.100' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.firstName} {user.lastName}
                      {user.role && ['root_admin', 'system_admin', 'support_agent', 'developer', 'ux_designer'].includes(user.role) && (
                        <AdminIcon fontSize="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'grey.100' }}>{user.email}</TableCell>
                  <TableCell sx={{ color: 'grey.100' }}>
                    {user.familyCode ? (
                      <Chip label={user.familyCode} size="small" color="primary" variant="outlined" />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.400' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'grey.100' }}>
                    {user.role ? (
                      <Chip
                        label={getSystemRoleDisplayName(user.role)}
                        size="small"
                        color="secondary"
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.400' }}>User</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'grey.100' }}>
                    {user.isActive === false ? (
                      <Chip label="Inactive" size="small" color="error" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'grey.100' }}>
                    {user.createdAt ? (
                      <Typography variant="body2" sx={{ color: 'grey.100' }}>
                        {formatDistanceToNow(user.createdAt.toDate(), { addSuffix: true })}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'grey.400' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'grey.100' }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewUser(user)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManageUsers && (
                      <Tooltip title="Edit User">
                        <IconButton size="small" onClick={() => handleEditUser(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageUsers && user.email && (
                      <Tooltip title="Send Password Reset">
                        <IconButton size="small" onClick={() => handleSendPasswordReset(user)} color="info">
                          <ResetPasswordIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageSystemAdmins && !user.role && (
                      <Tooltip title="Promote to System Admin">
                        <IconButton size="small" onClick={() => handlePromoteUser(user)} color="primary">
                          <PromoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageUsers && (
                      <Tooltip title={user.isActive === false ? 'Reactivate' : 'Deactivate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(user)}
                          color={user.isActive === false ? 'success' : 'error'}
                        >
                          {user.isActive === false ? <ActivateIcon fontSize="small" /> : <DeactivateIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedUser.firstName} {selectedUser.lastName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedUser.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Display Name</Typography>
                  <Typography variant="body1">{selectedUser.displayName || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                  <Typography variant="body1">{selectedUser.phoneNumber || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Family Code</Typography>
                  <Typography variant="body1">{selectedUser.familyCode || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                  <Typography variant="body1">{selectedUser.role ? getSystemRoleDisplayName(selectedUser.role) : 'User'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Typography variant="body1">
                    {selectedUser.isActive === false ? 'Inactive' : 'Active'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedUser.id}</Typography>
                </Grid>
                {selectedUser.createdAt && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                      {selectedUser.createdAt.toDate().toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                {selectedUser.role && selectedUser.systemPermissions && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>System Permissions</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Object.entries(selectedUser.systemPermissions)
                        .filter(([key, value]) => value === true)
                        .map(([key]) => (
                          <Chip key={key} label={key} size="small" color="primary" variant="outlined" />
                        ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canManageUsers && selectedUser?.email && (
            <Button
              startIcon={<ResetPasswordIcon />}
              onClick={() => handleSendPasswordReset(selectedUser)}
              color="info"
            >
              Send Password Reset
            </Button>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editForm.firstName || ''}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editForm.lastName || ''}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={editForm.displayName || ''}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editForm.email || ''}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editForm.phoneNumber || ''}
                  onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Promote to System Admin Dialog */}
      <Dialog open={promoteDialogOpen} onClose={() => setPromoteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Promote to System Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedUser && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You are promoting <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> to a system administrator role.
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>System Role</InputLabel>
              <Select
                value={promoteForm.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                label="System Role"
              >
                <MenuItem value={SYSTEM_ROLES.SYSTEM_ADMIN}>System Admin</MenuItem>
                <MenuItem value={SYSTEM_ROLES.SUPPORT_AGENT}>Support Agent</MenuItem>
                <MenuItem value={SYSTEM_ROLES.DEVELOPER}>Developer</MenuItem>
                <MenuItem value={SYSTEM_ROLES.UX_DESIGNER}>UX Designer</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" gutterBottom>Permissions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the permissions for this role. You can customize individual permissions.
            </Typography>

            <FormGroup>
              {Object.entries(SYSTEM_PERMISSIONS).map(([key, value]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={promoteForm.permissions[value] === true}
                      onChange={() => handlePermissionToggle(value)}
                    />
                  }
                  label={value}
                />
              ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePromote} variant="contained" color="primary">
            Promote User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create System Account Dialog */}
      <Dialog open={createAccountDialogOpen} onClose={() => setCreateAccountDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create System Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Create a new system administrator account. This account will NOT be tied to any family and will have system-level access.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  helperText="A temporary password will be generated and sent to this email"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>System Role</InputLabel>
                  <Select
                    value={createForm.role}
                    onChange={(e) => handleCreateRoleChange(e.target.value)}
                    label="System Role"
                  >
                    <MenuItem value={SYSTEM_ROLES.SYSTEM_ADMIN}>System Admin (Full Access)</MenuItem>
                    <MenuItem value={SYSTEM_ROLES.SUPPORT_AGENT}>Support Agent (Assigned Families)</MenuItem>
                    <MenuItem value={SYSTEM_ROLES.DEVELOPER}>Developer (Read-Only)</MenuItem>
                    <MenuItem value={SYSTEM_ROLES.UX_DESIGNER}>UX Designer (Read-Only)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>Permissions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Default permissions are pre-selected based on the role. You can customize them below.
            </Typography>

            <FormGroup>
              {Object.entries(SYSTEM_PERMISSIONS).map(([key, value]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={createForm.permissions[value] === true}
                      onChange={() => handleCreatePermissionToggle(value)}
                    />
                  }
                  label={value}
                />
              ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateAccountDialogOpen(false)} disabled={creatingAccount}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAccount}
            variant="contained"
            color="primary"
            disabled={creatingAccount || !createForm.email || !createForm.firstName || !createForm.lastName}
            startIcon={creatingAccount ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creatingAccount ? 'Creating...' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemUserManagement;
